import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * Guard that ensures the user has an active subscription.
 * Runs AFTER authGuard (user is authenticated) and BEFORE apiKeysGuard (credentials check).
 *
 * Subscription status is read from AuthService._user signal,
 * populated by GET /auth/me which works for all subscription statuses
 * (the /auth prefix skips tenantContext middleware in api-gateway).
 */
export const subscriptionGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const user = auth.user();
  if (!user) {
    // AuthService not initialized — should not happen since authGuard runs first,
    // but defensive: redirect to login to re-authenticate
    return router.createUrlTree(['/login']);
  }

  const ALLOWED_STATUSES: string[] = ['active', 'trialing'];

  // A lapsed trial is ONE fact with TWO representations (V19, 2026-10-03): a `trialing` row
  // whose date has passed (pre-sweep / sweep-down) and the `expired` row the trial-expiry sweep
  // writes. It is checked FIRST so both take the same branch — the allowlist below would send
  // `expired` straight to /billing and a never-picked post-trial tenant would skip the picker
  // steps that exist to run BEFORE subscribing.
  // Use != null instead of truthiness — an empty string or 0 would be falsy
  // but still indicate a malformed value, not a missing value.
  if (
    (user.subscriptionStatus === 'trialing' || user.subscriptionStatus === 'expired') &&
    user.trialExpiresAt != null &&
    user.trialExpiresAt !== '' &&
    new Date(user.trialExpiresAt) < new Date()
  ) {
    // Post-trial: force marketplace selection if none configured.
    // The tenant must select which marketplaces to monitor before subscribing.
    const selected = auth.selectedMarketplaces();
    if (selected.length === 0) {
      return router.createUrlTree(['/marketplace-picker']);
    }
    return router.createUrlTree(['/billing']);
  }

  if (!ALLOWED_STATUSES.includes(user.subscriptionStatus)) {
    // pending_payment, past_due, cancelled, suspended — and an `expired` row whose
    // trial_expires_at is missing/unusable (the branch above could not date it) — go to billing.
    return router.createUrlTree(['/billing']);
  }

  return true;
};
